import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PageHero from '../../components/PageHero';
import { SkeletonGrid, EmptyState, ErrorState, Pagination } from '../../components/ui';
import { newsApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { formatDate, truncate } from '../../lib/format';

const FALLBACK_IMAGES = [
  '/img/story-a.jpg',
  '/img/story-b.jpg',
  '/img/community.jpg',
  '/img/unity-hands.jpg',
];

/** Best display URL for a news featured image (handles absolute + stored paths). */
function newsImage(item) {
  const path = item?.featured_image_path;
  if (path && /^https?:\/\//i.test(path)) return path;
  return item?.featured_image_url || assetUrl(path);
}

function NewsCard({ item, index }) {
  const to = `/news/${item.slug || item.id}`;
  const img = newsImage(item) || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
  return (
    <article className="card card-hover group flex h-full flex-col overflow-hidden">
      <Link to={to} className="block">
        <div className="aspect-[4/3] overflow-hidden bg-maroon-50">
          <img
            src={img}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        {item.published_at && (
          <p className="mb-2 text-xs text-ink-muted">{formatDate(item.published_at)}</p>
        )}
        <h3 className="mb-2 font-display text-lg font-bold leading-snug text-ink transition-colors group-hover:text-maroon-700">
          <Link to={to}>{item.title}</Link>
        </h3>
        <p className="text-sm text-ink-muted line-clamp-3">
          {truncate(item.excerpt || item.body, 120)}
        </p>
        <Link to={to} className="link-caps mt-auto pt-4">
          Read more <span aria-hidden="true">&rsaquo;</span>
        </Link>
      </div>
    </article>
  );
}

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const [state, setState] = useState({ loading: true, error: null, items: [], meta: null });

  const page = Number(params.get('page') || 1);
  const q = params.get('q') || '';

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    newsApi
      .list({ page, per_page: 9, search: q || undefined })
      .then((res) => active && setState({ loading: false, error: null, items: res.items, meta: res.meta }))
      .catch((error) => active && setState({ loading: false, error, items: [], meta: null }));
    return () => {
      active = false;
    };
  }, [page, q]);

  function update(next) {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v) merged.set(k, v);
      else merged.delete(k);
    });
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  }

  return (
    <>
      <Seo
        title="News & Highlights"
        description="The latest news, announcements, and highlights from Zonta District 17 Area 1."
      />

      <PageHero
        eyebrow="Stay Informed"
        title="News & Highlights"
        subtitle="Announcements, milestones, and highlights from across District 17 Area 1 and our clubs."
        image="/img/community.jpg"
      />

      <section className="container-page py-12">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: query });
            }}
            className="flex w-full max-w-md gap-2"
          >
            <input
              className="input"
              placeholder="Search news…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search news"
            />
            <button type="submit" className="btn-primary px-4">
              Search
            </button>
          </form>
          {q && (
            <p className="text-sm text-ink-muted">
              Showing results for “<span className="font-medium text-ink">{q}</span>”
            </p>
          )}
        </div>

        {state.loading ? (
          // Mirrors the loaded `sm:grid-cols-2 lg:grid-cols-3` grid below.
          <SkeletonGrid count={6} columns="1-2-3" gap={6} />
        ) : state.error ? (
          <ErrorState error={state.error} onRetry={() => update({})} />
        ) : state.items.length === 0 ? (
          <EmptyState
            icon={q ? '🔍' : '🗞️'}
            title={q ? 'No matching news' : 'No news yet'}
            message={
              q
                ? `No results for “${q}”. Try a different search.`
                : 'Check back soon for updates and highlights from our clubs.'
            }
            action={
              q ? (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    update({ q: '' });
                  }}
                  className="btn-outline"
                >
                  Clear search
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {state.items.map((item, i) => (
                <NewsCard key={item.id} item={item} index={i} />
              ))}
            </div>
            <Pagination meta={state.meta} onChange={(p) => update({ page: String(p) })} />
          </>
        )}
      </section>
    </>
  );
}
